import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  AlertTriangle,
  Calendar,
  DollarSign,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Phone,
  Sun,
  Droplets,
  Sparkles,
  Shield,
  FileText,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

// 30 June 2026 · 5:00 pm AEST (UTC+10) — official Solar Victoria deadline.
const DEADLINE = new Date('2026-06-30T17:00:00+10:00');

const useCountdown = (target) => {
  const calc = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0, expired: true };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      mins: Math.floor((diff / 60000) % 60),
      secs: Math.floor((diff / 1000) % 60),
      expired: false,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return t;
};

const CountdownBlock = ({ value, label }) => (
  <div className="text-center" data-testid={`countdown-${label.toLowerCase()}`}>
    <div className="bg-[#3D5A47] rounded-2xl px-3 sm:px-5 py-3 sm:py-4 shadow-lg min-w-[64px] sm:min-w-[88px]">
      <div className="text-3xl sm:text-4xl font-black text-[#B5D43B] tabular-nums leading-none">
        {String(value).padStart(2, '0')}
      </div>
    </div>
    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-[#3D5A47]/70 mt-2 font-semibold">
      {label}
    </div>
  </div>
);

const VicRebateDeadlinePage = () => {
  const t = useCountdown(DEADLINE);
  const ctaHref = '/get-estimate?source=vic-rebate-deadline';

  return (
    <div className="min-h-screen">
      <SEO
        title="VIC Solar &amp; Hot Water Rebates — Apply by 30 June 2026"
        description="The Victorian Solar Homes Program income cap drops from $210,000 to $150,000 on 1 July 2026. Households earning $150k–$210k must apply by 5pm 30 June 2026 to lock in their rebate. Get a free Supa Energy Hub estimate."
      />
      <Header />
      <main>
        {/* HERO — urgency-first, light background */}
        <section
          className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]"
        >
          <div
            className="absolute inset-0 opacity-[0.18] pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          {/* Soft lime accent blob top-right */}
          <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              {/* Left column — copy + CTAs */}
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-3 py-1 mb-4">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-[11px] font-bold tracking-wider uppercase text-red-700">
                    Victoria · Deadline · 5 pm 30 June 2026
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight">
                  VIC Solar Rebate Income Cap is Dropping —{' '}
                  <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md inline-block leading-tight">
                    Apply by 30 June 2026
                  </span>{' '}
                  or Miss Out.
                </h1>

                <p className="text-base sm:text-lg text-gray-700 mt-5 max-w-2xl leading-relaxed">
                  From <strong>1 July 2026</strong>, the household income cap for the Victorian
                  Solar Homes Program drops from <strong>$210,000</strong> to{' '}
                  <strong>$150,000</strong>. Households earning $150k–$210k still qualify{' '}
                  <em>today</em> — but only if your application is in by{' '}
                  <strong>5 pm Tuesday, 30 June 2026</strong>.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Link to={ctaHref}>
                    <Button
                      className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-sm sm:text-base font-semibold px-5 py-5 rounded-xl shadow-lg shadow-[#3D5A47]/20"
                      data-testid="hero-cta-estimate"
                    >
                      Lock In My Rebate — Free 30-sec Estimate
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0478453548">
                    <Button
                      variant="outline"
                      className="bg-white border-[#3D5A47]/20 text-[#3D5A47] hover:bg-[#f8faf5] gap-2 text-sm sm:text-base px-5 py-5 rounded-xl"
                      data-testid="hero-cta-call"
                    >
                      <Phone className="w-4 h-4" /> Talk to Us — 0478 453 548
                    </Button>
                  </a>
                </div>

                <p className="text-[11px] text-gray-500 mt-5">
                  Source:{' '}
                  <a
                    href="https://www.solar.vic.gov.au/solar-homes-program-continuing-provide-targeted-assistance"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[#3D5A47]"
                  >
                    Solar Victoria · Solar Homes Program announcement, 29 May 2026
                  </a>
                  .
                </p>
              </div>

              {/* Right column — countdown card */}
              <div className="lg:col-span-5">
                <div className="bg-white rounded-3xl shadow-xl border border-[#B5D43B]/30 p-6 sm:p-8 relative">
                  <div className="absolute -top-3 left-6 bg-[#B5D43B] text-[#1a2820] text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
                    ⏱ Countdown
                  </div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1 mt-2">
                    Time remaining to apply under the $210k cap
                  </p>
                  <p className="text-sm text-[#3D5A47] font-bold mb-5">
                    5 pm Tuesday, 30 June 2026
                  </p>
                  <div className="grid grid-cols-4 gap-2 sm:gap-3" data-testid="rebate-countdown">
                    <CountdownBlock value={t.days} label="Days" />
                    <CountdownBlock value={t.hours} label="Hours" />
                    <CountdownBlock value={t.mins} label="Mins" />
                    <CountdownBlock value={t.secs} label="Secs" />
                  </div>
                  <div className="mt-5 pt-5 border-t border-gray-100 flex items-center gap-3 text-xs text-gray-600">
                    <div className="w-9 h-9 rounded-full bg-[#B5D43B]/20 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-[#3D5A47]" />
                    </div>
                    <span>
                      <strong className="text-[#3D5A47]">Supa Energy Hub</strong> submits your
                      full application weeks before the cut-off.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT'S CHANGING — Before vs After */}
        <section className="py-20 bg-[#f8faf5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">
                What's changing on 1 July 2026
              </span>
              <h2 className="heading-2 text-[#3D5A47]">
                One number is changing. It could cost a household up to{' '}
                <span className="text-[#B5D43B]">$2,800</span> in rebates.
              </h2>
              <p className="body-large text-gray-600 mt-4">
                Rebate values aren't changing — but eligibility is. The combined household
                income cap is dropping by <strong>$60,000</strong>, locking out tens of
                thousands of Victorian homes that qualify today.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* BEFORE */}
              <div className="bg-white rounded-2xl border-2 border-[#3D5A47] p-8 shadow-md relative" data-testid="rebate-card-now">
                <div className="absolute top-0 left-8 -translate-y-1/2 bg-[#3D5A47] text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  Until 5 pm · 30 June 2026
                </div>
                <div className="flex items-center gap-2 mb-3 text-[#3D5A47]">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase tracking-wide">You qualify if…</span>
                </div>
                <p className="text-5xl font-black text-[#3D5A47] mb-2">
                  ≤ $210k<span className="text-2xl text-gray-400">/yr</span>
                </p>
                <p className="text-gray-600 mb-6">
                  Combined household income (including renters' household).
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" />
                    <span>Up to <strong>$1,400 solar PV rebate</strong> + matching interest-free loan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" />
                    <span>Up to <strong>$1,400 hot water rebate</strong> (locally made)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" />
                    <span>Up to <strong>$1,000 hot water rebate</strong> (other eligible products)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#B5D43B] mt-0.5 flex-shrink-0" />
                    <span>Applies to <strong>owner-occupiers + rental properties</strong></span>
                  </li>
                </ul>
              </div>

              {/* AFTER */}
              <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 shadow-md relative" data-testid="rebate-card-after">
                <div className="absolute top-0 left-8 -translate-y-1/2 bg-gray-700 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                  From 1 July 2026
                </div>
                <div className="flex items-center gap-2 mb-3 text-gray-500">
                  <XCircle className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase tracking-wide">New eligibility cap</span>
                </div>
                <p className="text-5xl font-black text-gray-700 mb-2">
                  ≤ $150k<span className="text-2xl text-gray-400">/yr</span>
                </p>
                <p className="text-gray-600 mb-6">
                  Combined household income — <strong>$60,000 lower</strong> than today.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span>Households earning <strong>$150k–$210k</strong> become ineligible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>Rebate values stay the same for those still eligible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>27,000 additional hot water rebates available in 2026–27</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>Solstice Energy CNG customers remain exempt from the cap</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* WHO SHOULD ACT NOW */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#B5D43B]/15 via-white to-[#B5D43B]/5 border-2 border-[#B5D43B]/40 rounded-3xl p-8 md:p-12">
              <div className="grid md:grid-cols-3 gap-8 items-center">
                <div className="md:col-span-1 flex justify-center">
                  <div className="relative">
                    <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-[#B5D43B] flex items-center justify-center shadow-xl">
                      <Users className="w-16 h-16 sm:w-20 sm:h-20 text-[#1a2820]" />
                    </div>
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-12 h-12 flex items-center justify-center shadow-md animate-pulse">
                      ACT
                    </span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <span className="inline-block text-[#3D5A47] font-semibold text-xs tracking-widest uppercase mb-2">
                    Most Affected
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#3D5A47] mb-3">
                    Earning $150,000 – $210,000 per year as a household?
                  </h2>
                  <p className="text-base sm:text-lg text-gray-700 mb-5">
                    You are eligible <strong>today</strong> — but you will be{' '}
                    <strong className="text-red-600">ineligible from 1 July 2026</strong>. Don't
                    wait. We can have your full Solar Victoria application submitted with{' '}
                    <strong>weeks to spare</strong>.
                  </p>
                  <Link to={ctaHref}>
                    <Button
                      className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 px-6 py-5 rounded-xl"
                      data-testid="act-cta-estimate"
                    >
                      Start My Free Estimate <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AVAILABLE REBATES */}
        <section className="py-20 bg-[#f8faf5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">
                Rebates we lock in for you
              </span>
              <h2 className="heading-2 text-[#3D5A47]">
                Up to <span className="text-[#B5D43B]">$2,800</span> back per household — plus
                an interest-free loan.
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Sun,
                  title: 'Solar PV Rebate',
                  amount: '$1,400',
                  detail:
                    'For owner-occupiers and rental properties. Plus an equivalent interest-free loan to reduce upfront costs even further.',
                },
                {
                  icon: Droplets,
                  title: 'Hot Water Rebate (Locally Made)',
                  amount: '$1,400',
                  detail:
                    'For Australian-made eligible heat pump hot water systems. Pairs with the federal STC discount automatically.',
                },
                {
                  icon: Sparkles,
                  title: 'Hot Water Rebate (Other Eligible)',
                  amount: '$1,000',
                  detail:
                    'For other Solar Victoria-approved hot water products. Still stackable with federal STC and any installer discounts.',
                },
              ].map((r, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`rebate-tile-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <r.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <p className="text-3xl font-black text-[#3D5A47] mb-1">
                    Up to {r.amount}
                  </p>
                  <h3 className="font-bold text-[#3D5A47] mb-2">{r.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{r.detail}</p>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 mt-8 max-w-3xl">
              *Rebate values are unchanged from the current Solar Homes Program settings.
              Stackable with federal STCs. Final eligibility and amounts confirmed by Solar
              Victoria during application. Subject to product approval and program funding.
            </p>
          </div>
        </section>

        {/* TIMELINE */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">
                The Timeline
              </span>
              <h2 className="heading-2 text-[#3D5A47]">Don't wait. Here's why every week matters.</h2>
            </div>

            <ol className="relative border-l-2 border-[#B5D43B]/30 pl-8 space-y-10">
              {[
                {
                  date: 'Today',
                  title: 'Get a free estimate (30 seconds)',
                  detail:
                    'Tell us your address, household size and current hot water/solar setup. We confirm your eligibility and rebate amounts on the spot.',
                  icon: FileText,
                },
                {
                  date: 'Within 1–2 weeks',
                  title: 'Site survey + Solar Victoria application submitted',
                  detail:
                    'We handle the full Solar Victoria application paperwork on your behalf and submit it well before the 30 June cut-off.',
                  icon: Shield,
                },
                {
                  date: '5 pm · Tuesday 30 June 2026',
                  title: 'Final deadline to apply under the $210k cap',
                  detail:
                    'Submissions must be IN FULL by this time. The cap drops to $150k the next day. No exceptions.',
                  icon: AlertTriangle,
                  highlight: true,
                },
                {
                  date: '1 July 2026 onward',
                  title: 'New $150k income cap takes effect',
                  detail:
                    'Households over $150k combined income lose rebate eligibility. Solstice CNG customers remain exempt.',
                  icon: Calendar,
                },
              ].map((step, i) => (
                <li key={i} className="relative">
                  <span
                    className={`absolute -left-[42px] top-0 w-9 h-9 rounded-full flex items-center justify-center border-4 border-white ${
                      step.highlight ? 'bg-red-500 text-white' : 'bg-[#B5D43B] text-[#1a2820]'
                    } shadow-md`}
                  >
                    <step.icon className="w-4 h-4" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#B5D43B]">
                    {step.date}
                  </span>
                  <h3
                    className={`text-lg font-bold mt-1 ${
                      step.highlight ? 'text-red-600' : 'text-[#3D5A47]'
                    }`}
                  >
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{step.detail}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* HOW SEH HELPS */}
        <section className="py-20 bg-[#3D5A47] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">
                How Supa Energy Hub helps you beat the deadline
              </span>
              <h2 className="heading-2 text-white">
                We handle the paperwork. You lock in the savings.
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: FileText, t: 'Rebate paperwork done for you', d: 'Solar Victoria, federal STCs and any state incentives — all filed by our team.' },
                { icon: DollarSign, t: 'Stackable rebates', d: 'VIC Solar Homes + federal STCs combined to give you maximum savings upfront.' },
                { icon: Shield, t: 'Solar Victoria-approved installers', d: 'CEC-accredited installers and approved product lists only.' },
                { icon: Clock, t: 'Survey-to-install in 2–4 weeks', d: 'We move fast specifically to beat seasonal deadlines like this one.' },
              ].map((c, i) => (
                <div
                  key={i}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
                  data-testid={`seh-help-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#B5D43B]/20 flex items-center justify-center mb-4">
                    <c.icon className="w-5 h-5 text-[#B5D43B]" />
                  </div>
                  <h3 className="font-bold mb-2">{c.t}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">
              FAQ
            </span>
            <h2 className="heading-2 text-[#3D5A47] mb-10">Quick answers</h2>

            <div className="space-y-4">
              {[
                {
                  q: 'I earn over $210,000 — am I still eligible?',
                  a: 'No, the program is income-tested. The current cap is $210,000 combined household income, dropping to $150,000 from 1 July 2026. The exception is Solstice Energy customers transitioning from CNG, who are exempt from the cap.',
                },
                {
                  q: 'What if my application is partly submitted by 30 June 2026?',
                  a: 'Solar Victoria requires applications to be submitted IN FULL by 5 pm on 30 June 2026 to lock in the current $210k cap. Partial submissions will be assessed under the new $150k cap. We aim to get clients submitted weeks ahead.',
                },
                {
                  q: 'Can I claim both the solar PV rebate AND the hot water rebate?',
                  a: 'Yes — they are separate rebates and can be claimed together if you meet eligibility for each. The income cap applies to your overall household income, not per rebate.',
                },
                {
                  q: 'Is the interest-free loan still available?',
                  a: 'Yes. An interest-free loan equivalent to the solar PV rebate (up to $1,400) is still available alongside the rebate, helping reduce upfront install costs even further.',
                },
                {
                  q: 'What about renters?',
                  a: 'Solar rebates for rental properties are also subject to the same income eligibility cap (the combined household income of the rental occupants). Landlords applying on behalf of tenants need to provide tenant household income evidence.',
                },
              ].map((f, i) => (
                <details
                  key={i}
                  className="group bg-[#f8faf5] rounded-xl border border-gray-100 overflow-hidden"
                  data-testid={`faq-${i}`}
                >
                  <summary className="cursor-pointer p-5 font-semibold text-[#3D5A47] flex items-center justify-between gap-4 list-none">
                    <span>{f.q}</span>
                    <span className="text-[#B5D43B] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>

            <p className="text-xs text-gray-500 mt-10">
              All figures, dates and eligibility criteria sourced from the official{' '}
              <a
                href="https://www.solar.vic.gov.au/solar-homes-program-continuing-provide-targeted-assistance"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3D5A47] underline hover:text-[#B5D43B]"
              >
                Solar Victoria announcement
              </a>
              {' '}(29 May 2026). Always confirm individual eligibility with Solar Victoria during application.
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 bg-gradient-to-br from-[#B5D43B] to-[#a3c235]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-black text-[#1a2820] mb-4">
              Don't watch your rebate vanish on 30 June.
            </h2>
            <p className="text-lg text-[#1a2820]/80 mb-8 max-w-2xl mx-auto">
              Free, no-obligation estimate. 30 seconds. We handle every form, every signature
              and every deadline — so you don't have to.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={ctaHref}>
                <Button
                  className="bg-[#1a2820] hover:bg-[#3D5A47] text-white gap-2 text-base font-semibold px-7 py-6 rounded-xl shadow-xl"
                  data-testid="final-cta-estimate"
                >
                  Lock In My Rebate <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0478453548">
                <Button
                  variant="outline"
                  className="bg-white/40 border-[#1a2820]/30 text-[#1a2820] hover:bg-white gap-2 text-base px-7 py-6 rounded-xl"
                  data-testid="final-cta-call"
                >
                  <Phone className="w-4 h-4" /> Call 0478 453 548
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VicRebateDeadlinePage;
